import { useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import { API } from "../../api/Api";
import { SessionContext } from "../../common/contexts/SessionContext";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { showtoast } from "../../common/overlays/Toasts/showtoast";
import FullDetail from "../../common/panels/FullDetail/FullDetail";
import { getDetailLink, getIndexRoute, ResourceName } from "../../main/Routing";
import { DownloadDataset } from "../common/DownloadDataset/DownloadDataset";
import { datasetsConstants } from "../../api/Datasets";
import { SharedContentModal } from "../../common/helpers/EntityModals/SharedContentModal";
import { Button } from "../../common/buttons/Button/Button";
import { DetailsPageTopControlsView } from "../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { EntityDetailProps } from "../../common/entity/EntityInterfaces";
import { EntityRestoreButton } from "../../common/entity/entityComponents/EntityRestoreButton";
import { EntityDeleteButton } from "../../Entities/GenericComponents/common/GenericEntityDetailPage";
import { EntityEditButton } from "../../common/entity/entityComponents/EntityEditButton";
import { EntityComplianceDropdown } from "../../common/entity/entityComponents/EntityComplianceDropdown";
import { GenericEntityExportButton } from "../../Entities/GenericComponents/common/GenericEntityExportButton";
import { customImportConstants, CustomImportResult } from "../../api/CustomImports";
import { usePostMutation } from "../../api/BaseEntityApi";

interface Props {
  api: API;
  viewViewer: boolean;
}
export const Navigation = ({
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  api,
  viewViewer,
  entityTypeId,
}: Props & EntityDetailProps<"datasets">) => {
  const history = useHistory();
  const { route, session } = useContext(SessionContext);
  // const [showDatasets, setShowDatasets] = useState(false);

  const { editMutationAsync, isLoadingEditMutation } = entityApi;

  // const setShowDatasetsCallback = useCallback(() => {
  //   setShowDatasets(() => !showDatasets);
  // }, [showDatasets]);

  const claimHandler = useCallback(
    () => history.push(route(`${getIndexRoute(entityConstants.frontendIndexRoute)}/${entity.id}/claim`)),
    [entity.id, entityConstants.frontendIndexRoute, history, route]
  );

  const unclaimHandler = useCallback(async () => {
    if (entity) {
      await editMutationAsync(
        { body: { ...entity, claimed: false }, id: entity.id },
        {
          onSuccess: () => {
            showtoast("success", `Unclaimed Dataset-ID: ${entity.id}.`);
          },
        }
      ).catch(() => {});
    }
  }, [entity, editMutationAsync]);

  const { mutateAsync: bulkApplyCustomImportMutationAsync, isLoading: isLoadingBulkApplyCustomImportMutation } =
    usePostMutation<CustomImportResult[], { datasetIds: number[] }>({
      resource: "custom_imports/datasets" as ResourceName,
    });

  return (
    <FullDetail.Navigation style={{ display: "flex", flexFlow: "row-nowrap", alignItems: "center" }}>
      <DetailsPageTopControlsView
        entityTypeId="datasets"
        entity={entity}
        entityConstants={datasetsConstants}
        containerStyle={{ borderBottom: "0px" }}
        controlsLeft={
          <>
            {viewViewer && (
              <button
                className={"btn btn-primary"}
                onClick={() => {
                  // context.fullscreen.set({
                  //   ...context.fullscreen.get,
                  //   state: !context.fullscreen.get.state,
                  //   component: "",
                  // });
                  history.push(route(getDetailLink("datasets", entity.id)));
                }}
                style={{ margin: 0 }}
              >
                <LucideIcon name="chevrons-left" /> Show details
              </button>
            )}
          </>
        }
        controls={
          <>
            <EntityRestoreButton
              entity={entity}
              entityConstants={entityConstants}
              entityApi={entityApi}
              permissions={permissions}
            />
            <EntityEditButton
              entity={entity}
              entityConstants={entityConstants}
              permissions={permissions}
              routes={routes}
            />
            {!entity.claimed && !entity.isDeleted && (
              <button
                className={`btn btn-success`}
                title={"Claim dataset"}
                onClick={async () => await claimHandler()}
                disabled={!permissions.canEdit(entity)}
              >
                <LucideIcon name="flag" /> Claim
              </button>
            )}
            <DownloadDataset api={api} id={entity.id} />
          </>
        }
        controlsDropdown={
          <>
            <li>
              <SharedContentModal
                datasetIds={[entity.id]}
                disabled={!session?.permissions.add_bundles || entity.isDeleted}
              />
            </li>
            {entity.claimed && (
              <li>
                <Button
                  className={`btn btn-sm btn-ghost-danger`}
                  title={"Unclaim dataset"}
                  onClick={async () => await unclaimHandler()}
                  disabled={!permissions.canEdit(entity)}
                  loading={isLoadingEditMutation}
                >
                  <LucideIcon name="circle-x" /> Unclaim
                </Button>
              </li>
            )}
            <li>
              <GenericEntityExportButton entity={entity} entityTypeId={entityTypeId} />
            </li>
            <EntityComplianceDropdown entity={entity} entityTypeId={entityTypeId} showSignButton />
            {session?.permissions.logsadmin && (
              <li>
                <Button
                  className="btn btn-sm btn-ghost-success"
                  title={`Bulk apply ${customImportConstants.entitySingular}`}
                  onClick={async () => {
                    await bulkApplyCustomImportMutationAsync(
                      { body: { datasetIds: [entity.id] } },
                      {
                        onSuccess: (data) => {
                          showtoast("success", `Successfully applied ${customImportConstants.entityPlural}.`);
                        },
                      }
                    ).catch(() => {});
                  }}
                  loading={isLoadingBulkApplyCustomImportMutation}
                >
                  <LucideIcon name={customImportConstants.icon} /> Apply {customImportConstants.entitySingular}
                </Button>
              </li>
            )}
            <li className="divider" />
            <li>
              <EntityDeleteButton
                entity={entity}
                entityTypeId={entityTypeId}
                btnClassName={(_, deletePermanently) =>
                  `btn btn-sm btn-ghost-${deletePermanently ? "danger" : "warning"}`
                }
              />
            </li>
          </>
        }
      />
    </FullDetail.Navigation>
  );
};
